import { useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import * as Sentry from '@sentry/react';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';
import { InputActionMeta } from 'react-select';
import { toast } from 'react-toastify';

import { GetPayeesForCompanyDocument, PayeeType } from '@willow/graphql-iso/src/app';
import { CompanyId, PayeeId } from '@willow/types-iso';

import { WillowMultiSelect } from './WillowSelect';

type PayeeSelectOption = { label: string; value: PayeeId };

const PAYEE_LIST_LIMIT = 250;
const SEARCH_DEBOUNCE_MS = 300;

const toOption = (payee: { id: PayeeId; name: string; externalPayeeId: string }): PayeeSelectOption => ({
  value: payee.id,
  label: `${payee.name} | ${payee.externalPayeeId}`,
});

type Props<T extends FieldValues> = {
  companyId: CompanyId;
  fieldName: FieldPathByValue<T, PayeeId[]>;
  control: Control<T>;
  payeeTypes: PayeeType[];
  hideErrorMessage?: boolean;
  placeholder?: string;
  disabled?: boolean;
};

export function ControlledPayeeMultiSelect<T extends FieldValues>({
  companyId,
  fieldName,
  control,
  payeeTypes,
  hideErrorMessage = false,
  placeholder = 'Select payees',
  disabled,
}: Props<T>) {
  const {
    field: { value, onChange },
    formState: { errors },
  } = useController({ control, name: fieldName });

  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  // searching in the payee input changes the list so we cache any returned results so that selected options aren't lost
  const [knownById, setKnownById] = useState<Map<PayeeId, PayeeSelectOption>>(new Map());

  useEffect(() => {
    const debouncedSetQuery = debounce(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    debouncedSetQuery();

    return () => debouncedSetQuery.cancel();
  }, [searchInput]);

  const { data, previousData, error } = useQuery(GetPayeesForCompanyDocument, {
    variables: { companyId, input: { take: PAYEE_LIST_LIMIT, skip: 0, payeeTypes, query: query || undefined } },
    onCompleted: (result) => {
      const results = result?.company?.payees?.results ?? [];
      if (results.length === 0) return;

      setKnownById((prev) => {
        const next = new Map(prev);
        results.forEach((payee) => next.set(payee.id, toOption(payee)));

        return next;
      });
    },
  });

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
      toast.error('Failed to load payees. Please try again.');
    }
  }, [error]);

  // fallback on previous data when data is empty because of a new query
  const activeData = data ?? previousData;
  const options = (activeData?.company?.payees?.results || []).map(toOption);

  const selectedIds = (value as PayeeId[] | undefined) ?? [];
  const selectedOptions = selectedIds.map((id) => knownById.get(id) ?? { value: id, label: id });

  const selectedSummary = useCallback((count: number) => `${count} ${count === 1 ? 'payee' : 'payees'} selected`, []);

  const handleChange = useCallback(
    (selected: readonly PayeeSelectOption[] | null) => onChange(selected ? selected.map((option) => option.value) : []),
    [onChange],
  );

  const handleInputChange = useCallback((input: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      setSearchInput(input);
    } else if (meta.action === 'menu-close' || meta.action === 'input-blur') {
      setSearchInput('');
    }
  }, []);

  return (
    <>
      <WillowMultiSelect
        placeholder={placeholder}
        isDisabled={disabled}
        options={options}
        value={selectedOptions}
        // The server has already filtered by search text; don't double-filter client-side.
        filterOption={null}
        inputValue={searchInput}
        onInputChange={handleInputChange}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        selectedSummary={selectedSummary}
        onChange={handleChange}
      />
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="mt-1 u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
